/* eslint-disable react/require-default-props */
'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';

import {
  Card,
  CardFooter,
  CardHeader,
} from '@heroui/react';

import { TEXT_SHARP_STYLES, MOBILE_OVERLAY_GRADIENT, DESKTOP_OVERLAY_GRADIENT } from '@/utils/cardStyles';

import '../cards.css';

export interface RegularCardProps {
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  imageCover?: string;
  bgImgSize?: 'lg' | 'md' | 'sm';
  isFooterBlurred?: boolean;
  isBlurred?: boolean;
  isPressable?: boolean;
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  isDisabled?: boolean;
  onPress?: () => void;
  onPressStart?: () => void;
  onPressEnd?: () => void;
  onPressChange?: () => void;
  onPressUp?: () => void;
  disableAnimation?: boolean;
  content?: React.ReactNode;
  contentAlign: 'center' | 'left' | 'right';
  style?: React.CSSProperties;
  width?: number;
  height?: number;
  mobileWidth?: number;
  mobileHeight?: number;
  imageGrayscale?: boolean;
}

export const RegularCard = ({
  headerContent = undefined,
  footerContent = undefined,
  imageCover = undefined,
  bgImgSize = 'md',
  isFooterBlurred = false,
  isBlurred = false,
  isPressable = false,
  shadow = 'none',
  isDisabled = false,
  onPress = () => {},
  onPressStart = () => {},
  onPressEnd = () => {},
  onPressChange = () => {},
  onPressUp = () => {},
  disableAnimation = false,
  content = undefined,
  contentAlign = 'center',
  style = {},
  width = undefined,
  height = undefined,
  mobileWidth = undefined,
  mobileHeight = undefined,
  imageGrayscale = false,
}: RegularCardProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    const checkMobile = () => {
      return window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };
    setIsMobile(checkMobile());
  }, []);

  const getResponsiveWidth = () => {
    if (isMobile) {
      return mobileWidth || width || 140;
    }
    return width || 200;
  };

  const getResponsiveHeight = () => {
    if (isMobile) {
      return mobileHeight || height || 140;
    }
    return height || 200;
  };

  const getImageClasses = () => {
    const currentWidth = getResponsiveWidth();
    const currentHeight = getResponsiveHeight();

    if (currentWidth && currentHeight) {
      return 'object-cover';
    }

    switch (bgImgSize) {
      case 'lg':
        return 'object-cover w-full h-full';
      case 'md':
        return 'object-cover w-1/2 h-auto';
      case 'sm':
        return 'object-cover w-1/4 h-auto';
      default:
        return 'object-cover w-full h-full';
    }
  };

  const cardStyle = {
    ...style,
    ...(getResponsiveWidth() && getResponsiveHeight() ? {
      width: `${getResponsiveWidth()}px`,
      height: `${getResponsiveHeight()}px`,
      minWidth: `${getResponsiveWidth()}px`,
      minHeight: `${getResponsiveHeight()}px`,
    } : {}),
  };

  const imageStyle = {
    ...(getResponsiveWidth() && getResponsiveHeight() ? {
      width: `${getResponsiveWidth()}px`,
      height: `${getResponsiveHeight()}px`,
      minWidth: `${getResponsiveWidth()}px`,
      minHeight: `${getResponsiveHeight()}px`,
    } : {}),
  };

  return (
    <Card
      className={`card--${contentAlign} relative rounded-xl select-none`}
      isFooterBlurred={isFooterBlurred}
      isBlurred={isBlurred}
      isPressable={isPressable}
      shadow={shadow}
      isDisabled={isDisabled}
      onPress={onPress}
      onPressStart={onPressStart}
      onPressEnd={onPressEnd}
      onPressChange={onPressChange}
      onPressUp={onPressUp}
      disableAnimation={disableAnimation}
      style={cardStyle}
    >
      {headerContent && (
        <CardHeader className="absolute z-30 top-1 flex-col !items-start">
          {headerContent}
        </CardHeader>
      )}
      {imageCover && (
        <Image
          alt="Card background"
          src={imageCover}
          className={`${getImageClasses()} select-none`}
          width={getResponsiveWidth()}
          height={getResponsiveHeight()}
          style={{
            ...imageStyle,
            filter: imageGrayscale ? 'grayscale(100%)' : 'none',
          }}
          draggable={false}
        />
      )}
      {content}
      {footerContent && (
        <>
          <div 
            className="absolute bottom-0 left-0 right-0 h-20 z-[5]"
            style={isHydrated ? {
              background: isMobile ? MOBILE_OVERLAY_GRADIENT : DESKTOP_OVERLAY_GRADIENT,
              ...(isMobile ? {} : {
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
              }),
            } : {
              background: DESKTOP_OVERLAY_GRADIENT,
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            }}
          />
          <CardFooter 
            className="absolute z-20 bottom-1 flex-col -pt-4" 
            style={{ 
              ...TEXT_SHARP_STYLES,
              isolation: 'isolate',
            }}
          >
            {footerContent}
          </CardFooter>
        </>
      )}
    </Card>
  );
};
